/**
 * Creative Performance — the reusable section.
 *
 * Rendered by every individual platform Analytics page that has a creative
 * reporting contract, and by no page that does not. It answers the third
 * question a platform page owes its reader, after how the platform is
 * performing and how that is changing: which creatives produced it.
 *
 * It deliberately owns no data fetching and no platform branching. Scope
 * (advertiser, platform, window) arrives as a projection the page already
 * resolved; presentation comes from the shared view model. Connector-specific
 * behavior belongs in the backend adapter, not here.
 *
 * Styling reuses the page's own `ppx-` primitives — panel, header, state and
 * numeric type — rather than a parallel stylesheet that would drift from them.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import CreativePreviewTile from "../components/CreativeAssetPreviewTile";
import {
  CREATIVE_PERFORMANCE_STATE,
  UNAVAILABLE_DISPLAY,
  describeWindow,
} from "./creativePerformanceModel";

function CreativeMetricCell({ cell }) {
  return (
    <div
      className="ppx-creative-metric"
      data-metric={cell.key}
      data-availability={cell.availability}
    >
      <span className="ppx-creative-metric-label">{cell.label}</span>
      <span
        className={`ppx-creative-metric-value ppx-numeric${
          cell.isAvailable ? "" : " ppx-creative-metric-value--absent"
        }`}
        /*
         * The dash is meaningless to a screen reader on its own, so absence is
         * announced in words. A reported value needs no such annotation.
         */
        title={cell.note || undefined}
        aria-label={cell.isAvailable ? undefined : `${cell.label}: not reported`}
      >
        {cell.display}
        {cell.isPartial && (
          <span className="ppx-creative-partial-flag" aria-hidden="true">
            *
          </span>
        )}
      </span>
    </div>
  );
}

function CreativeCard({ creative, onOpen }) {
  return (
    <li className="ppx-creative-card" data-creative-key={creative.creativeKey}>
      <button
        type="button"
        className="ppx-creative-card-trigger"
        onClick={() => onOpen(creative)}
        aria-label={`Open analytics detail for ${creative.name}`}
      >
        <span className="ppx-creative-thumb">
          <CreativePreviewTile
            assetName={creative.name}
            fallbackApiPath={creative.previewPath}
            className="ppx-creative-thumb-media"
            fallbackClassName="ppx-creative-thumb-fallback"
            thumbnailMode
            thumbnailTimeoutMs={6000}
            loadingLabel="Loading preview"
          />
        </span>
        <span className="ppx-creative-identity">
          <span className="ppx-creative-name">{creative.name}</span>
          {creative.shape && <span className="ppx-creative-shape">{creative.shape}</span>}
          {creative.approvalLabel && (
            <span className="ppx-creative-approval">{creative.approvalLabel}</span>
          )}
        </span>
      </button>

      {creative.observedLabels.length > 0 && (
        <ul className="ppx-creative-labels">
          {creative.observedLabels.map((label) => (
            <li key={label.key} className="ppx-creative-label">
              {label.label}
            </li>
          ))}
        </ul>
      )}

      <div className="ppx-creative-metrics">
        {creative.cells.map((cell) => (
          <CreativeMetricCell key={cell.key} cell={cell} />
        ))}
      </div>
    </li>
  );
}

/*
 * The analytics drill-down. Deliberately read-only: a larger preview, the
 * metadata and the observed performance for the window already on screen.
 * Editing, versioning and approval stay in Creative Studio — this is not a
 * second one.
 */
function CreativeDetailDialog({ creative, window: reportingWindow, platformLabel, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!creative) return null;

  return (
    <div
      className="ppx-creative-detail-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="ppx-creative-detail"
        role="dialog"
        aria-modal="true"
        aria-label={`${creative.name} analytics detail`}
        ref={dialogRef}
      >
        <div className="ppx-creative-detail-header">
          <div>
            <h3 className="ppx-creative-detail-title">{creative.name}</h3>
            <p className="ppx-creative-detail-meta">
              {[platformLabel, creative.shape, creative.approvalLabel].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button
            type="button"
            className="ppx-creative-detail-close"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close creative detail"
          >
            ✕
          </button>
        </div>

        <div className="ppx-creative-detail-body">
          <div className="ppx-creative-detail-preview">
            <CreativePreviewTile
              assetName={creative.name}
              fallbackApiPath={creative.previewPath}
              className="ppx-creative-detail-media"
              fallbackClassName="ppx-creative-thumb-fallback"
              controls
              loadingLabel="Loading preview"
            />
          </div>

          <div className="ppx-creative-detail-facts">
            <dl className="ppx-creative-detail-metrics">
              {creative.cells.map((cell) => (
                <div key={cell.key} data-availability={cell.availability}>
                  <dt>{cell.label}</dt>
                  <dd className="ppx-numeric">
                    {cell.display}
                    {cell.note && <span className="ppx-creative-detail-note">{cell.note}</span>}
                  </dd>
                </div>
              ))}
            </dl>
            {describeWindow(reportingWindow) && (
              <p className="ppx-creative-detail-window">
                Reporting window <span className="ppx-numeric">{describeWindow(reportingWindow)}</span>
              </p>
            )}
            {/*
             * Version attribution is stated rather than implied. The connector
             * names an ad, which identifies the creative but not which frozen
             * version served, and saying so is more useful than a version
             * number that would be a guess.
             */}
            {creative.versionAttribution === "asset_level" && (
              <p className="ppx-creative-detail-attribution" role="note">
                Performance is attributed to this creative. The platform reports delivery per ad, which
                does not identify which creative version served.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreativePerformancePanel({ viewModel, onSortChange }) {
  const [openCreative, setOpenCreative] = useState(null);
  const handleClose = useCallback(() => setOpenCreative(null), []);

  if (!viewModel) return null;

  const { state, isReporting, creatives, sort, sortOptions, reconciliation, source } = viewModel;
  const hasPartial = creatives.some((creative) =>
    creative.cells.some((cell) => cell.isPartial)
  );

  return (
    <section className="ppx-panel ppx-panel--creative" aria-label="Creative performance">
      <div className="ppx-panel-header">
        <div>
          <h2 className="ppx-panel-title">{viewModel.title}</h2>
          <p className="ppx-panel-subtitle">{viewModel.subtitle}</p>
        </div>

        {isReporting && sortOptions.length > 0 && (
          <label className="ppx-creative-sort">
            <span className="ppx-creative-sort-label">Rank by</span>
            <select
              className="ppx-select"
              value={sort?.key || ""}
              onChange={(event) => onSortChange?.(event.target.value)}
            >
              {/*
               * Only dimensions this platform's source actually reports are
               * offered, so a page can never sort by a metric it cannot show.
               */}
              {sortOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isReporting ? (
        <>
          <ul className="ppx-creative-grid">
            {creatives.map((creative) => (
              <CreativeCard key={creative.creativeKey} creative={creative} onOpen={setOpenCreative} />
            ))}
          </ul>

          <div className="ppx-creative-footnotes">
            {source?.platform && (
              <p className="ppx-creative-footnote">
                Creative delivery reported by {source.platform}.{" "}
                {describeWindow(viewModel.window) && (
                  <>
                    Window <span className="ppx-numeric">{describeWindow(viewModel.window)}</span>.
                  </>
                )}
              </p>
            )}
            <p className="ppx-creative-footnote">
              {UNAVAILABLE_DISPLAY} marks a metric this platform did not report for that creative. It is
              not zero.
            </p>
            {hasPartial && (
              <p className="ppx-creative-footnote">
                * marks a partial total — some delivery rows for that creative did not report the metric.
              </p>
            )}
            {reconciliation?.note && (
              <p className="ppx-creative-footnote ppx-creative-footnote--warn" role="note">
                {reconciliation.note}
              </p>
            )}
          </div>
        </>
      ) : (
        <div
          className="ppx-state-panel"
          role={state === CREATIVE_PERFORMANCE_STATE.LOADING ? "status" : undefined}
          data-creative-state={state}
        >
          <h3 className="ppx-state-title">{viewModel.stateTitle}</h3>
          <p className="ppx-state-body">{viewModel.stateBody}</p>
        </div>
      )}

      {openCreative && (
        <CreativeDetailDialog
          creative={openCreative}
          window={viewModel.window}
          platformLabel={viewModel.platformLabel}
          onClose={handleClose}
        />
      )}
    </section>
  );
}
